import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Button, Modal } from "@mui/material";

import { Loading } from "commons";
import { useComponentState } from "commons/src/helpers/CustomHooks";
import { DONE } from "commons/src/helpers/RequestHelpers";

import { deliveryOptionProp } from "../../../constants/propTypes";
import { OptionList } from "./OptionList";
import { Message } from "../../../components/Message";
import { ERROR as ERROR_SEVERITY } from "../../../helpers/ErrorHelpers";
import { updateCorrespondenceDeliveryPreference } from "../../../components/DataOperations";

export function DocumentPreferencesEdit({
    allDeliveryOptions,
    currentPreference = [],
    isAdviser = false,
    handlePrefrencesUpdate = () => {},
    handleClose = () => {},
    isOpen = false,
    deliveryOptionsFetchState = "",
    getOptions = () => {}
}) {
    const [selectedOption, setSelectedOption] = useState({});
    const [currentState, changeStateTo, { LOADING, ERROR, SUCCESS, DEFAULT }] = useComponentState();
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (currentPreference) {
            setSelectedOption(currentPreference.deliveryOption);
        }
    }, [currentPreference, changeStateTo, DEFAULT]);

    const handleOptionChange = e =>
        setSelectedOption(allDeliveryOptions.find(option => option.codeCode === e.target.value));

    const handleSave = async () => {
        changeStateTo(LOADING);
        const { status, message } = await updateCorrespondenceDeliveryPreference({
            documentType: currentPreference.documentType,
            deliveryOption: {
                codeCode: selectedOption.codeCode,
                codeType: selectedOption.codeType
            }
        });
        changeStateTo(status === DONE ? SUCCESS : ERROR);

        if (status === DONE) {
            handlePrefrencesUpdate(
                "Your changes to document delivery preferences have been updated successfully"
            );
        }
        setMessage(message);
    };

    const onClose = () => {
        changeStateTo(DEFAULT);
        setSelectedOption(currentPreference.deliveryOption);
        setMessage("");
        handleClose();
    };

    const isOptionsFetchError = deliveryOptionsFetchState === ERROR;
    const showMessage = currentState === ERROR || isOptionsFetchError;
    return (
        <StyledModal data-testid="edit-preferences-modal" open={isOpen} onClose={onClose}>
            <EditContainer>
                {showMessage && (
                    <MessageContainer>
                        <Message
                            severity={ERROR_SEVERITY}
                            text={
                                isOptionsFetchError
                                    ? "There was a problem while fetching available delivery options."
                                    : message
                            }
                            action={isOptionsFetchError ? { retry: () => getOptions() } : null}
                        />
                    </MessageContainer>
                )}
                {deliveryOptionsFetchState === LOADING ? (
                    <BigLoading size={20} data-testid="loading-delivery-options-indicator" />
                ) : (
                    <SubSection>
                        <OptionList
                            documentType={currentPreference?.documentType?.codeDescription}
                            allDeliveryOptions={allDeliveryOptions}
                            selectedOption={selectedOption?.codeCode}
                            onChange={handleOptionChange}
                        />
                    </SubSection>
                )}
                <ButtonContainer>
                    <ControlBtn onClick={onClose} color="primary" data-testid="cancel-button">
                        Cancel
                    </ControlBtn>
                    <ControlBtn
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        data-testid="save-button"
                        disabled={
                            currentState === LOADING ||
                            isAdviser ||
                            deliveryOptionsFetchState !== SUCCESS
                        }
                    >
                        {currentState === LOADING ? (
                            <Loading size={12} data-testid="switch-client-loading" />
                        ) : (
                            "Save"
                        )}
                    </ControlBtn>
                </ButtonContainer>
            </EditContainer>
        </StyledModal>
    );
}

const StyledModal = styled(Modal)`
    display: flex;
    margin-right: 1rem;
    margin-left: 1rem;
`;

const MessageContainer = styled.div`
    padding: 1rem;
    background: ${props => props.theme.tertiary};
`;

export const EditContainer = styled.div`
    margin: auto;
    width: 100%;
    min-width: 280px;
    background: ${props => props.theme.tertiary};
    overflow: auto;
    padding-bottom: 1rem;
    max-width: 750px;
    outline: none;
`;

export const ControlBtn = styled(Button)`
    width: 40%;
    max-width: 280px;
`;

export const BigLoading = styled(Loading)`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
`;

export const ButtonContainer = styled.div`
    margin-bottom: 0;
    margin-top: 1.5rem;
    display: flex;
    justify-content: center;
    width: 100%;

    @media (max-width: 715px) {
        justify-content: space-around;
    }
`;

const SubSection = styled.div`
    background: ${props => props.theme.tertiary};
    padding: 1.5em;
`;

DocumentPreferencesEdit.propTypes = {
    currentPreference: PropTypes.shape({
        documentType: deliveryOptionProp,
        deliveryOption: deliveryOptionProp
    }),
    allDeliveryOptions: PropTypes.arrayOf(deliveryOptionProp),
    isAdviser: PropTypes.bool,
    handlePrefrencesUpdate: PropTypes.func,
    handleClose: PropTypes.func,
    isOpen: PropTypes.bool,
    deliveryOptionsFetchState: PropTypes.string,
    getOptions: PropTypes.func
};
